import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Command } from 'cmdk';
import { useUIStore } from '../stores/ui';
import { useChatStore } from '../stores/chat';
import { api } from '../services/api';
import {
  Search,
  LayoutDashboard,
  CheckSquare,
  Calendar,
  Clock,
  Settings,
  Plus,
  Sparkles,
  Send,
  Loader2,
} from 'lucide-react';

export function CommandPalette() {
  const { isCommandPaletteOpen, setCommandPaletteOpen } = useUIStore();
  const { addMessage, setLoading, isLoading, conversationId, setConversationId } = useChatStore();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [mode, setMode] = useState<'commands' | 'chat'>('commands');

  // Reset on close
  useEffect(() => {
    if (!isCommandPaletteOpen) {
      setQuery('');
      setMode('commands');
    }
  }, [isCommandPaletteOpen]);

  // Detect chat mode (starts with /)
  useEffect(() => {
    if (query.startsWith('/') || mode === 'chat') {
      setMode('chat');
    }
  }, [query, mode]);

  const handleSendMessage = useCallback(async () => {
    const message = query.startsWith('/') ? query.slice(1).trim() : query.trim();
    if (!message || isLoading) return;

    // Add user message
    addMessage({ role: 'user', content: message });
    setQuery('');
    setLoading(true);

    try {
      const response = await api.sendMessage(message, conversationId || undefined);
      
      addMessage({
        role: 'assistant',
        content: response.message,
        agentUsed: response.agentUsed,
        actions: response.actions,
      });

      if (response.conversationId) {
        setConversationId(response.conversationId);
      }
    } catch (error) {
      addMessage({
        role: 'assistant',
        content: `Error: ${error instanceof Error ? error.message : 'Failed to send message'}`,
      });
    } finally {
      setLoading(false);
    }
  }, [query, isLoading, conversationId, addMessage, setLoading, setConversationId]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (mode === 'chat' && e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
    },
    [mode, handleSendMessage]
  );

  const navigationItems = [
    { icon: LayoutDashboard, label: 'Go to Dashboard', path: '/', shortcut: 'G D' },
    { icon: CheckSquare, label: 'Go to Tasks', path: '/tasks', shortcut: 'G T' },
    { icon: Calendar, label: 'Go to Calendar', path: '/calendar', shortcut: 'G C' },
    { icon: Clock, label: 'Go to Planner', path: '/planner', shortcut: 'G P' },
    { icon: Settings, label: 'Go to Settings', path: '/settings', shortcut: 'G S' },
  ];

  const actionItems = [
    { icon: Plus, label: 'Create new task', action: 'create-task' },
    { icon: Calendar, label: 'Schedule event', action: 'create-event' },
    { icon: Clock, label: 'Plan my day', action: 'plan-day' },
  ];

  return (
    <Command.Dialog
      open={isCommandPaletteOpen}
      onOpenChange={setCommandPaletteOpen}
      className="fixed inset-0 z-50"
    >
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Dialog */}
      <div className="fixed left-1/2 top-[20%] w-full max-w-xl -translate-x-1/2 animate-slide-down">
        <div className="overflow-hidden rounded-xl border border-border bg-surface shadow-2xl">
          {/* Input */}
          <div className="flex items-center gap-3 border-b border-border px-4 py-3">
            {mode === 'chat' ? (
              <Sparkles className="h-4 w-4 text-accent" />
            ) : (
              <Search className="h-4 w-4 text-text-tertiary" />
            )}
            <Command.Input
              value={query}
              onValueChange={setQuery}
              onKeyDown={handleKeyDown}
              placeholder={mode === 'chat' ? 'Ask your assistant...' : 'Search commands or type / to chat...'}
              className="flex-1 bg-transparent text-sm text-text-primary outline-none placeholder:text-text-tertiary"
            />
            {mode === 'chat' && (
              <button
                onClick={handleSendMessage}
                disabled={isLoading || !query.trim()}
                className="rounded-lg p-1.5 text-text-secondary hover:bg-surface-hover hover:text-accent disabled:opacity-50"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </button>
            )}
          </div>

          {/* Content */}
          <Command.List className="max-h-80 overflow-auto p-2">
            {mode === 'commands' ? (
              <>
                <Command.Empty className="py-6 text-center text-sm text-text-tertiary">
                  No results found.
                </Command.Empty>

                {/* Chat suggestion */}
                <Command.Group heading="Assistant">
                  <Command.Item
                    onSelect={() => setMode('chat')}
                    className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm text-text-secondary hover:bg-surface-hover hover:text-text-primary aria-selected:bg-surface-hover aria-selected:text-text-primary"
                  >
                    <Sparkles className="h-4 w-4 text-accent" />
                    <span className="flex-1">Chat with Assistant</span>
                    <kbd className="kbd">/</kbd>
                  </Command.Item>
                </Command.Group>

                {/* Navigation */}
                <Command.Group heading="Navigation">
                  {navigationItems.map(({ icon: Icon, label, path, shortcut }) => (
                    <Command.Item
                      key={path}
                      onSelect={() => {
                        navigate(path);
                        setCommandPaletteOpen(false);
                      }}
                      className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm text-text-secondary hover:bg-surface-hover hover:text-text-primary aria-selected:bg-surface-hover aria-selected:text-text-primary"
                    >
                      <Icon className="h-4 w-4" />
                      <span className="flex-1">{label}</span>
                      <kbd className="kbd">{shortcut}</kbd>
                    </Command.Item>
                  ))}
                </Command.Group>

                {/* Actions */}
                <Command.Group heading="Actions">
                  {actionItems.map(({ icon: Icon, label, action }) => (
                    <Command.Item
                      key={action}
                      onSelect={() => {
                        // Handle action
                        setMode('chat');
                        if (action === 'create-task') {
                          setQuery('/Create a new task: ');
                        } else if (action === 'create-event') {
                          setQuery('/Schedule an event: ');
                        } else if (action === 'plan-day') {
                          setQuery('/Plan my day for today');
                        }
                      }}
                      className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm text-text-secondary hover:bg-surface-hover hover:text-text-primary aria-selected:bg-surface-hover aria-selected:text-text-primary"
                    >
                      <Icon className="h-4 w-4" />
                      <span className="flex-1">{label}</span>
                    </Command.Item>
                  ))}
                </Command.Group>
              </>
            ) : (
              <div className="py-4 text-center text-sm text-text-tertiary">
                <p>Type your message and press Enter to send</p>
                <p className="mt-1 text-2xs">
                  Press <kbd className="kbd">Esc</kbd> to exit chat mode
                </p>
              </div>
            )}
          </Command.List>

          {/* Footer */}
          <div className="flex items-center justify-between border-t border-border px-4 py-2 text-2xs text-text-tertiary">
            <div className="flex items-center gap-4">
              <span>
                <kbd className="kbd">↑↓</kbd> Navigate
              </span>
              <span>
                <kbd className="kbd">Enter</kbd> Select
              </span>
              <span>
                <kbd className="kbd">Esc</kbd> Close
              </span>
            </div>
            <span className="text-accent">Personal Assistant</span>
          </div>
        </div>
      </div>
    </Command.Dialog>
  );
}

